import { useState } from 'react';
import { Button, Input, Alert, useToast } from '@dreamtree-org/twreact-ui';
import { PageHeader, SectionCard, KeyValue } from '@/components';
import { errorText } from '@/lib/format';
import { useMutation } from '@/lib/useQuery';
import { useVendorSession } from '../VendorApp';
import { PASSWORD_HINT } from '../lib/password';

/** The vendor's own business details and password. Email changes go through the store. */
export default function Account() {
  const { toast } = useToast();
  const { vendor, reload } = useVendorSession();
  const profileM = useMutation('vendorUpdateProfile');
  const passwordM = useMutation('vendorChangePassword');
  const [profile, setProfile] = useState({ business_name: vendor.business_name, contact_name: vendor.contact_name, phone: vendor.phone, gstin: vendor.gstin });
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [error, setError] = useState({ profile: '', password: '' });

  const saveProfile = async (e) => {
    e.preventDefault();
    setError({ ...error, profile: '' });
    try {
      await profileM.run(profile);
      toast.success('Details saved');
      reload();
    } catch (ex) {
      setError({ ...error, profile: errorText(ex) });
    }
  };
  const savePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) return setError({ ...error, password: 'The new passwords do not match.' });
    setError({ ...error, password: '' });
    try {
      await passwordM.run({ current: pw.current, next: pw.next });
      setPw({ current: '', next: '', confirm: '' });
      toast.success('Password changed', 'Other devices were signed out.');
    } catch (ex) {
      setError({ ...error, password: errorText(ex) });
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Account" subtitle={`Vendor ${vendor.id}`} />
      <SectionCard title="Sign-in">
        <KeyValue items={[{ label: 'Email', value: vendor.email }, { label: 'Status', value: vendor.status }]} />
      </SectionCard>
      <SectionCard title="Business details">
        <form className="space-y-3" onSubmit={saveProfile}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="Business name" value={profile.business_name} onChange={(e) => setProfile({ ...profile, business_name: e.target.value })} required maxLength={160} />
            <Input label="Contact person" value={profile.contact_name} onChange={(e) => setProfile({ ...profile, contact_name: e.target.value })} required maxLength={120} />
            <Input label="Phone" inputMode="tel" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} required />
            <Input label="GSTIN" value={profile.gstin} onChange={(e) => setProfile({ ...profile, gstin: e.target.value.toUpperCase() })} maxLength={15} helperText="Optional" />
          </div>
          {error.profile ? <Alert variant="error" title="Not saved">{error.profile}</Alert> : null}
          <div className="flex justify-end"><Button type="submit" loading={profileM.loading}>Save details</Button></div>
        </form>
      </SectionCard>
      <SectionCard title="Change password">
        <form className="space-y-3" onSubmit={savePassword}>
          <Input label="Current password" type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required />
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="New password" type="password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} required helperText={PASSWORD_HINT} />
            <Input label="Repeat new password" type="password" autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required />
          </div>
          {error.password ? <Alert variant="error" title="Not changed">{error.password}</Alert> : null}
          <div className="flex justify-end"><Button type="submit" loading={passwordM.loading}>Change password</Button></div>
        </form>
      </SectionCard>
    </div>
  );
}
